import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../../../../../components-v2/Theme/theme';

/**
 * SectionHeader styles
 *
 * The section header is rendered as a single horizontal title BAR at the top of the
 * SectionCard. Background switches by save state (mirrors the question-card
 * left-border stripe rules) so the user can read each section's state at a glance
 * even when collapsed:
 *   - New / dirty → brandPrimary (blue) + white text
 *   - Saved      → successGreen + dark text (green is light enough for dark text)
 *   - Error      → destructive (red) + white text
 *
 * The inner content card carries the same state via its own left-border stripe
 * (see `SectionCard/styles.ts`); the bar's role is the title + …menu only.
 */

export const styles = StyleSheet.create({
	/** Title bar — horizontal strip across the top of the section. */
	bar: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		borderRadius: THEME.common.borderRadius,
		gap: THEME.spacing.sm,
	},
	/** Collapsed bar — clickable to expand. No background here; state classes own that. */
	barCollapsed: {
		cursor: 'pointer',
		':focus-visible': {
			outline: `2px solid ${THEME.colors.primary.main}`,
			outlineOffset: 2,
		},
	},
	/** New / dirty section — blue bar, white text. Default while authoring. */
	barNew: {
		backgroundColor: THEME.colors.primary.main,
		color: THEME.colors.primary.contrastText,
	},
	/** Persisted section with no pending edits — green bar, dark text for contrast. */
	barSaved: {
		backgroundColor: THEME.colors.success.main,
		color: THEME.colors.text.main,
	},
	/** Section with validation errors — red bar, white text. */
	barError: {
		backgroundColor: THEME.colors.danger.main,
		color: THEME.colors.danger.contrastText,
	},

	/**
	 * Open/close caret on the left of the bar. Points down when the section is
	 * expanded, rotates to point right when collapsed, with a short transition for
	 * a subtle open/close animation. Inherits the bar's text color. Decorative —
	 * the whole bar is the click target.
	 */
	chevron: {
		flexShrink: 0,
		color: 'inherit',
		transition: 'transform 0.2s ease',
	},
	/** Collapsed state — rotate the down-chevron to point right. */
	chevronCollapsed: {
		transform: 'rotate(-90deg)',
	},

	/** Title text — caps, semi-bold. Color flips white when expanded. */
	titleText: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: 600,
		letterSpacing: 0.5,
		textTransform: 'uppercase',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		flex: 1,
		minWidth: 0,
		margin: 0,
		color: 'inherit',
	},

	/** Ellipsis menu wrapper — keeps the menu trigger on the right of the bar. */
	actions: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
	},

	/**
	 * The dropdown trigger renders as a `<button>` whose user-agent `color` is
	 * `ButtonText` (typically black) — that breaks `currentColor` inheritance
	 * from the bar's text color. Force `color: inherit` so the ⋯ SVG (which we
	 * pass `fillColor='currentColor'` to) picks up the bar's state-driven text
	 * color (white on blue/red, dark `titles` on green).
	 */
	ellipsisTriggerInherit: {
		color: 'inherit',
	},

	/**
	 * Override the dropdown popup's minWidth so short items (View / Reorder /
	 * Duplicate / Delete) don't collapse the menu to text-width — gives a
	 * consistent visual target for the ⋯ menu across all section states.
	 */
	dropdownContentMinWidth: {
		minWidth: 140,
	},
});
